import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DashboardDataRaw, DateRange } from '../../types/Dashboard';
import { Panel, Badge, Button, IconSwap, ShareBar, easeStandard } from '../ui';
import { ChevronDownIcon, ChevronUpIcon } from '../icons';
import { formatCurrency, formatNumber } from './dashboardUi';
import {
  EXCLUSIVE_8080_BRACKETS,
  exclusive8080AutoRevenue,
  formatDateAR,
  lastDayOfMonth,
  manualOverrideForMonth,
} from './dashboardHelpers';

interface Props {
  data: DashboardDataRaw;
  range: DateRange;
  planName: string;
}

const th = 'pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400';
const thDetail = 'py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400';

const Exclusive8080Breakdown: React.FC<Props> = ({ data, range, planName }) => {
  const [open, setOpen] = useState(false);

  const { snapshot, override, auto, brackets, maxCount } = useMemo(() => {
    const snapshot = lastDayOfMonth(range.to);
    const override = manualOverrideForMonth(data.manualRevenueHistory[planName] ?? [], snapshot);
    const auto = exclusive8080AutoRevenue(data.premium, snapshot, planName);

    const byBracket = new Map<string, { count: number; value: number; subtotal: number }>();
    for (const b of EXCLUSIVE_8080_BRACKETS) {
      byBracket.set(`${b.min}-${b.max}`, { count: 0, value: b.value, subtotal: 0 });
    }
    for (const d of auto.details) {
      if (d.age === null) continue;
      const entry = byBracket.get(d.bracket);
      if (entry) {
        entry.count += 1;
        entry.subtotal += d.value;
      }
    }

    const brackets = Array.from(byBracket.entries());
    const maxCount = brackets.reduce((m, [, row]) => Math.max(m, row.count), 0);

    return { snapshot, override, auto, brackets, maxCount };
  }, [data, range, planName]);

  const totalSubs = auto.details.length;
  const plural = totalSubs === 1 ? '' : 's';

  return (
    <Panel
      title={`Desglose del revenue ${planName}`}
      subtitle={
        <>
          Foto al {formatDateAR(snapshot)} · {formatNumber(totalSubs)} suscriptor{plural} activo
          {plural}.{' '}
          {override
            ? 'Este mes tiene un revenue cargado a mano, así que el auto-calculado no se aplica.'
            : 'Sale de la edad de cada suscriptor: es una estimación, no un valor exacto.'}
        </>
      }
      actions={
        <Button
          variant="secondary"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          icon={
            <IconSwap swapKey={open ? 'up' : 'down'}>
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconSwap>
          }
        >
          {open ? 'Ocultar detalle' : 'Ver detalle'}
        </Button>
      }
    >
      {override && (
        <div className="mb-4 flex items-center gap-2">
          <Badge tone="warning">Override manual</Badge>
          <span className="text-xs text-slate-500">
            Se está usando {formatCurrency(override.monthly_amount)} en lugar del cálculo por edad.
          </span>
        </div>
      )}

      <div className="-mx-6 overflow-x-auto px-6">
        <table className="w-full min-w-[420px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left">
              <th className={`${th} pe-4`}>Tramo de edad</th>
              <th className={`${th} pe-4 text-right`}>Valor por sub.</th>
              <th className={`${th} pe-4 w-32`}>Suscriptores</th>
              <th className={`${th} text-right`}>Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {brackets.map(([label, row]) => (
              <tr
                key={label}
                className="border-b border-slate-100 transition-colors duration-100 ease-out last:border-0 hover:bg-slate-50/70"
              >
                <td className="py-2 pe-4 font-medium tabular-nums text-gappex-secondary">
                  {label} años
                </td>
                <td className="py-2 pe-4 text-right tabular-nums text-slate-500">
                  {formatCurrency(row.value)}
                </td>
                <td className="py-2 pe-4">
                  <ShareBar
                    pct={maxCount > 0 ? (row.count / maxCount) * 100 : 0}
                    color="#f59e0b"
                  />
                  <span className="mt-1 block text-[11px] tabular-nums text-slate-400">
                    {formatNumber(row.count)}
                  </span>
                </td>
                <td className="py-2 text-right font-medium tabular-nums text-slate-700">
                  {formatCurrency(row.subtotal)}
                </td>
              </tr>
            ))}
            {auto.missingBirthdate > 0 && (
              <tr className="border-b border-slate-100 last:border-0">
                <td className="py-2 pe-4">
                  <span className="flex items-center gap-2">
                    <span className="font-medium text-amber-700">Sin fecha de nacimiento</span>
                    <Badge tone="warning">no suma</Badge>
                  </span>
                </td>
                <td className="py-2 pe-4 text-right text-slate-400">—</td>
                <td className="py-2 pe-4">
                  <ShareBar
                    pct={maxCount > 0 ? (auto.missingBirthdate / maxCount) * 100 : 0}
                    color="#fcd34d"
                  />
                  <span className="mt-1 block text-[11px] tabular-nums text-amber-700">
                    {formatNumber(auto.missingBirthdate)}
                  </span>
                </td>
                <td className="py-2 text-right tabular-nums text-slate-400">
                  {formatCurrency(0)}
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-200 font-semibold text-gappex-secondary">
              <td className="pt-3 pe-4" colSpan={2}>
                Total auto-calculado
              </td>
              <td className="pt-3 pe-4 tabular-nums">{formatNumber(auto.withBirthdate)}</td>
              <td className="pt-3 text-right tabular-nums">{formatCurrency(auto.revenue)}</td>
            </tr>
            {override && (
              <tr className="text-amber-700">
                <td className="pt-1.5 pe-4" colSpan={3}>
                  Override manual aplicado
                </td>
                <td className="pt-1.5 text-right font-semibold tabular-nums">
                  {formatCurrency(override.monthly_amount)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="detalle"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.25, ease: easeStandard } }}
            exit={{ opacity: 0, y: -8, transition: { duration: 0.15, ease: 'easeOut' } }}
            className="mt-5"
          >
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Detalle por suscriptor
            </p>
            <div className="max-h-96 overflow-auto rounded-xl bg-slate-50 p-1">
              <table className="w-full min-w-[560px] text-xs">
                <thead className="sticky top-0 bg-slate-50">
                  <tr className="text-left">
                    <th className={`${thDetail} ps-3 pe-3`}>#</th>
                    <th className={`${thDetail} pe-3`}>Suscriptor</th>
                    <th className={`${thDetail} pe-3`}>DNI</th>
                    <th className={`${thDetail} pe-3`}>Fecha nac.</th>
                    <th className={`${thDetail} pe-3 text-right`}>Edad</th>
                    <th className={`${thDetail} pe-3`}>Tramo</th>
                    <th className={`${thDetail} pe-3 text-right`}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {auto.details.map((d, idx) => (
                    <tr
                      key={d.id}
                      className={`transition-colors duration-100 ease-out hover:bg-white ${
                        d.age === null ? 'text-amber-700' : 'text-slate-600'
                      }`}
                    >
                      <td className="py-1.5 ps-3 pe-3 tabular-nums text-slate-400">{idx + 1}</td>
                      <td className="py-1.5 pe-3 font-medium text-gappex-secondary">
                        {d.suscriptorName ?? '—'}
                      </td>
                      <td className="py-1.5 pe-3 font-mono text-[11px] tabular-nums">
                        {d.suscriptorDoc ?? '—'}
                      </td>
                      <td className="py-1.5 pe-3 tabular-nums">
                        {d.birthdate ? formatDateAR(d.birthdate) : '—'}
                      </td>
                      <td className="py-1.5 pe-3 text-right tabular-nums">{d.age ?? '—'}</td>
                      <td className="py-1.5 pe-3 tabular-nums">{d.bracket}</td>
                      <td className="py-1.5 pe-3 text-right tabular-nums">
                        {formatCurrency(d.value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Panel>
  );
};

export default Exclusive8080Breakdown;
